import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/lib/types';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, Scissors, PenTool, Box, Grid, ChevronRight, Sparkles } from 'lucide-react';

export function DefaultDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Redirect to appropriate section based on user's primary role
  useEffect(() => {
    if (!currentUser) return;

    // Define role to path mapping
    const rolePaths = [
      { role: UserRole.KITTING, path: '/dashboard/kitting' },
      { role: UserRole.FABRIC, path: '/dashboard/fabric' },
      { role: UserRole.POWDER, path: '/dashboard/metal' },
      { role: UserRole.ASSEMBLY, path: '/dashboard/assembly' },
      { role: UserRole.KANBAN, path: '/dashboard/overview' },
    ];

    // Find the first matching role
    for (const { role, path } of rolePaths) {
      if (currentUser.roles.includes(role)) {
        navigate(path);
        return;
      }
    }
  }, [currentUser, navigate]);

  const roleCards = [
    {
      role: UserRole.KITTING,
      title: 'Kitting',
      description: 'Manage kitting tasks and inventory preparation',
      icon: Package,
      path: '/dashboard/kitting',
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      role: UserRole.FABRIC,
      title: 'Fabric',
      description: 'Oversee fabric cutting and preparation processes',
      icon: Scissors,
      path: '/dashboard/fabric',
      gradient: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      role: UserRole.POWDER,
      title: 'Metal',
      description: 'Monitor metal work and powder coating operations',
      icon: PenTool,
      path: '/dashboard/metal',
      gradient: 'from-amber-500 to-amber-600',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600'
    },
    {
      role: UserRole.ASSEMBLY,
      title: 'Assembly',
      description: 'Coordinate final assembly and quality control',
      icon: Box,
      path: '/dashboard/assembly',
      gradient: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    },
    {
      role: UserRole.KANBAN,
      title: 'Overview',
      description: 'Access comprehensive production overview and planning',
      icon: Grid,
      path: '/dashboard/overview',
      gradient: 'from-teal-500 to-teal-600',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600'
    }
  ];

  const availableCards = roleCards.filter(card => 
    currentUser?.roles.includes(card.role)
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
      {/* Lightweight dotted background pattern */}
      <div className="fixed inset-0 opacity-[0.08] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='15' cy='15' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      <div className="space-y-8 animate-slide-up">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
          <Sparkles className="w-8 h-8 text-blue-500" />
          Welcome to Production Panel
        </div>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Select a production area to get started with your workflow management
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
        {availableCards.map((card) => {
          const IconComponent = card.icon;
          return (
            <Card 
              key={card.role}
              className="group cursor-pointer border-0 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden"
              onClick={() => navigate(card.path)}
            >
              <div className={`h-2 bg-gradient-to-r ${card.gradient}`} />
              <CardHeader className="relative">
                <div className="flex items-center justify-between">
                  <div className={`p-3 rounded-xl ${card.iconBg} transition-transform duration-200 group-hover:scale-110`}>
                    <IconComponent className={`h-6 w-6 ${card.iconColor}`} />
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-hover:translate-x-2" />
                </div>
                                  <CardTitle className="text-xl font-semibold group-hover:text-blue-600 transition-colors duration-200">
                  {card.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <CardDescription className="text-base leading-relaxed">
                  {card.description}
                </CardDescription>
                                  <Button 
                    variant="outline" 
                    className="w-full group-hover:bg-blue-50 group-hover:border-blue-200 group-hover:text-blue-700 transition-all duration-200"
                  >
                  View {card.title}
                  <ChevronRight className="w-4 h-4 ml-1 transition-transform duration-200 group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {availableCards.length === 0 && (
        <div className="text-center py-12">
          <div className="bg-slate-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-600 mb-2">No production areas available</h3>
          <p className="text-muted-foreground">Contact your administrator to assign production roles.</p>
        </div>
      )}
      </div>
    </div>
  );
} 